/**
 * Vocabularies mirrored from the API.
 *
 * Kept in step with `backend/src/constants/index.js` by hand, which is a real
 * maintenance cost and worth stating: adding a stake status there means adding
 * it here too, or the panel renders an unlabelled badge. The alternative — an
 * endpoint that serves enums — would be a request on every page load to learn
 * things that change once a year.
 */

export const ROLES = Object.freeze({
  ADMIN: 'admin',
  SUPER_ADMIN: 'super_admin',
});

export const ROLE_LABELS = Object.freeze({
  [ROLES.ADMIN]: 'Admin',
  [ROLES.SUPER_ADMIN]: 'Super admin',
  user: 'User',
});

/** Platform account states. Distinct from operator states — see the API. */
export const USER_STATUS = Object.freeze({
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
});

export const STAKE_STATUS = Object.freeze({
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
});

export const STAKING_TYPE = Object.freeze({
  FLEXIBLE: 'flexible',
  FIXED: 'fixed',
});

export const WITHDRAWAL_STATUS = Object.freeze({
  PENDING: 'pending',
  PAID: 'paid',
  EXPIRED: 'expired',
});

/**
 * The two pots income is withdrawn from.
 *
 * `staking` is daily ROI on an account's own stakes; `other` is referral, rank
 * and upline combined. The contract settles them through separate functions, so
 * they are never netted against each other.
 */
export const WITHDRAWAL_TYPE = Object.freeze({
  STAKING: 'staking',
  OTHER: 'other',
});

export const WITHDRAWAL_TYPE_LABELS = Object.freeze({
  [WITHDRAWAL_TYPE.STAKING]: 'Staking income',
  [WITHDRAWAL_TYPE.OTHER]: 'Other income',
});

/**
 * Where an offer claim sits in the fulfillment queue.
 *
 * `passed` is not a queue state an operator moves anything into — it is set
 * by the platform when an account no longer qualifies for a claim it had not
 * yet taken, and it is terminal.
 */
export const OFFER_ACHIEVEMENT_STATUS = Object.freeze({
  CLAIMED: 'claimed',
  PROCESSING: 'processing',
  DELIVERED: 'delivered',
  PASSED: 'passed',
});

export const OFFER_ACHIEVEMENT_STATUS_LABELS = Object.freeze({
  [OFFER_ACHIEVEMENT_STATUS.CLAIMED]: 'Claimed',
  [OFFER_ACHIEVEMENT_STATUS.PROCESSING]: 'Processing',
  [OFFER_ACHIEVEMENT_STATUS.DELIVERED]: 'Delivered',
  [OFFER_ACHIEVEMENT_STATUS.PASSED]: 'Passed',
});

/**
 * Where a registration reward claim sits in the fulfillment queue.
 *
 * Unlike an offer claim, there is no `passed` state here — a registration
 * reward claim only ever moves forward: claimed → processing → delivered.
 */
export const REGISTRATION_REWARD_STATUS = Object.freeze({
  CLAIMED: 'claimed',
  PROCESSING: 'processing',
  DELIVERED: 'delivered',
});

export const REGISTRATION_REWARD_STATUS_LABELS = Object.freeze({
  [REGISTRATION_REWARD_STATUS.CLAIMED]: 'Claimed',
  [REGISTRATION_REWARD_STATUS.PROCESSING]: 'Processing',
  [REGISTRATION_REWARD_STATUS.DELIVERED]: 'Delivered',
});

/** Which qualification a registration reward claim was earned under. */
export const REGISTRATION_REWARD_COMPONENT = Object.freeze({
  STAKE_100: 'stake_100',
  STAKE_500: 'stake_500',
  STAKE_1000: 'stake_1000',
  DIRECTS_BONUS: 'directs_bonus',
});

export const REGISTRATION_REWARD_COMPONENT_LABELS = Object.freeze({
  [REGISTRATION_REWARD_COMPONENT.STAKE_100]: 'Stake 100',
  [REGISTRATION_REWARD_COMPONENT.STAKE_500]: 'Stake 500',
  [REGISTRATION_REWARD_COMPONENT.STAKE_1000]: 'Stake 1000',
  [REGISTRATION_REWARD_COMPONENT.DIRECTS_BONUS]: 'Directs bonus',
});

/** The four append-only income ledgers. */
export const INCOME_STREAMS = Object.freeze({
  STAKING: 'staking',
  REFERRAL: 'referral',
  RANK: 'rank',
  UPLINE: 'upline',
});

export const INCOME_STREAM_VALUES = Object.freeze(Object.values(INCOME_STREAMS));

export const INCOME_STREAM_LABELS = Object.freeze({
  [INCOME_STREAMS.STAKING]: 'Staking',
  [INCOME_STREAMS.REFERRAL]: 'Referral',
  [INCOME_STREAMS.RANK]: 'Rank',
  [INCOME_STREAMS.UPLINE]: 'Upline',
});

/** One sentence per stream, shown above each ledger table. */
export const INCOME_STREAM_DESCRIPTIONS = Object.freeze({
  [INCOME_STREAMS.STAKING]:
    'Daily ROI on an account’s own stakes. Compounds on the base left in the stake, so each day pays on a larger figure than the last.',
  [INCOME_STREAMS.REFERRAL]:
    'A share of a direct referral’s own staking payout, at the earner’s referral rate. Level 1 only.',
  [INCOME_STREAMS.RANK]:
    'The differential between an earner’s rank rate and the rate of anyone ranked below them in the same chain. Paid on a downline staking payout.',
  [INCOME_STREAMS.UPLINE]:
    'A share of a downline staking payout at the rate for that depth — paid only while the earner meets the self-stake and qualifying-direct thresholds.',
});

export const RANK_MOVEMENT = Object.freeze({
  PROMOTION: 'promotion',
  DEMOTION: 'demotion',
});

/** Audit vocabulary, mirrored from `AUDIT_ACTIONS` / `AUDIT_RESOURCES`. */
export const AUDIT_ACTIONS = Object.freeze({
  USER_UPDATED: 'user.updated',
  USER_DELETED: 'user.deleted',
  USER_PURGED: 'user.purged',
  USER_RESTORED: 'user.restored',
  USER_IMPERSONATED: 'user.impersonated',
  PACKAGE_CREATED: 'package.created',
  PACKAGE_UPDATED: 'package.updated',
  RANK_CREATED: 'rank.created',
  RANK_UPDATED: 'rank.updated',
  UPLINE_LEVEL_CREATED: 'upline_level.created',
  UPLINE_LEVEL_UPDATED: 'upline_level.updated',
  SETTINGS_UPDATED: 'settings.updated',
  ADMIN_PASSWORD_CHANGED: 'admin.password_changed',
  OFFER_ACHIEVEMENT_PROCESSING: 'offer_achievement.processing',
  OFFER_ACHIEVEMENT_DELIVERED: 'offer_achievement.delivered',
  REGISTRATION_REWARD_PROCESSING: 'registration_reward.processing',
  REGISTRATION_REWARD_DELIVERED: 'registration_reward.delivered',
});

export const AUDIT_ACTION_LABELS = Object.freeze({
  [AUDIT_ACTIONS.USER_UPDATED]: 'User updated',
  [AUDIT_ACTIONS.USER_DELETED]: 'User soft-deleted',
  [AUDIT_ACTIONS.USER_PURGED]: 'User permanently deleted',
  [AUDIT_ACTIONS.USER_RESTORED]: 'User restored',
  [AUDIT_ACTIONS.USER_IMPERSONATED]: 'User impersonated',
  [AUDIT_ACTIONS.PACKAGE_CREATED]: 'Package created',
  [AUDIT_ACTIONS.PACKAGE_UPDATED]: 'Package updated',
  [AUDIT_ACTIONS.RANK_CREATED]: 'Rank created',
  [AUDIT_ACTIONS.RANK_UPDATED]: 'Rank updated',
  [AUDIT_ACTIONS.UPLINE_LEVEL_CREATED]: 'Upline level created',
  [AUDIT_ACTIONS.UPLINE_LEVEL_UPDATED]: 'Upline level updated',
  [AUDIT_ACTIONS.SETTINGS_UPDATED]: 'Settings updated',
  [AUDIT_ACTIONS.ADMIN_PASSWORD_CHANGED]: 'Operator password changed',
  [AUDIT_ACTIONS.OFFER_ACHIEVEMENT_PROCESSING]: 'Offer claim moved to processing',
  [AUDIT_ACTIONS.OFFER_ACHIEVEMENT_DELIVERED]: 'Offer claim marked delivered',
  [AUDIT_ACTIONS.REGISTRATION_REWARD_PROCESSING]: 'Registration reward moved to processing',
  [AUDIT_ACTIONS.REGISTRATION_REWARD_DELIVERED]: 'Registration reward marked delivered',
});

export const AUDIT_RESOURCES = Object.freeze({
  USER: 'user',
  PACKAGE: 'package',
  RANK: 'rank',
  UPLINE_LEVEL: 'upline_level',
  SETTINGS: 'settings',
  ADMIN: 'admin',
});

/**
 * Page sizes offered in the pagination control.
 *
 * Capped at 100 because the API caps it there (`PAGINATION_DEFAULTS.MAX_LIMIT`)
 * — offering 250 would produce a silently truncated page.
 */
export const PAGE_SIZES = Object.freeze([10, 25, 50, 100]);

export const DEFAULT_PAGE_SIZE = 25;

/**
 * How long a list stays fresh before TanStack Query will refetch it.
 *
 * Thirty seconds, and paired with `refetchOnWindowFocus: false` in the query
 * client. The API is rate limited (100 requests per 15 minutes by default), and
 * a panel that refetched every table on every tab switch would exhaust that
 * during a normal morning's work.
 */
export const STALE_TIME = 30_000;

/** Statistics are cached 60s server-side; matching it avoids pointless calls. */
export const STATS_STALE_TIME = 60_000;

/** Reference data changes about once a year. */
export const REFERENCE_STALE_TIME = 5 * 60_000;

export const APP_NAME = import.meta.env.VITE_APP_NAME || 'FRT Admin';

export const EXPLORER_URL = (import.meta.env.VITE_EXPLORER_URL || 'https://bscscan.com').replace(
  /\/+$/,
  '',
);
